import React, { useState } from 'react'
import { View, ViewStyle, TextStyle, TouchableOpacity, Text as RNText } from 'react-native'
import { observer } from 'mobx-react-lite'
import { useHistory } from 'react-router'
import { useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'

import { Header, color, Text, iconArrowBack, Button, DualOptionModal } from '@trivie/ui-mobile'
import { useStores } from '@trivie/core'

export const GDPRScreen = observer(() => {
  const { userStore } = useStores()
  const { user, logout, gdprConsent, reportProblem, getUserDataRequest } = userStore

  const history = useHistory()
  const goBack = () => history.goBack()

  const [showGDPRModal, setShowGDPRModal] = useState<boolean>(false)
  const [gdprModalMessage, setGDPRModalMessage] = useState<string | undefined>('')

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [deleteModalMessage, setDeleteModalMessage] = useState<string | undefined>('')

  const openDeleteModal = () => {
    setShowDeleteModal(true)
    setDeleteModalMessage(t('Settings.Account.deletionMessage'))
  }

  const openGDPRModal = () => {
    setShowGDPRModal(true)
    setGDPRModalMessage(t('Settings.dataRequestMessage'))
  }

  const confirmRequest = () => {
    getUserDataRequest()
    setGDPRModalMessage(
      t('Settings.dataRequestConfirmation', { support: process.env.REACT_APP_SUPPORT }),
    )
  }

  const confirmDeletion = () => {
    reportProblem(
      `NAME: ${user?.first_name} ${user?.last_name}; EMAIL: ${user?.email}; ID: ${user?.id} ACCESS CODE: ${userStore.accessCode} has requested that their account be deleted`,
      'New Support Message',
    )
    setDeleteModalMessage(
      t('Settings.Account.confirmDeletion', { support: process.env.REACT_APP_SUPPORT }),
    )
  }

  const revokeConsent = () => {
    if (user) {
      gdprConsent(user.id, {
        consent_granted: false,
        timestamp: DateTime.now().toUTC().toISO(),
      }).then(() => logout())
    }
  }

  const { t } = useTranslation()

  return (
    <>
      <View style={FULL}>
        <View style={CONTAINER}>
          <Header
            leftIcon={iconArrowBack}
            onLeftPress={goBack}
            center
            style={HEADER}
            titleStyle={HEADER_TITLE}
            headerTx="Settings.gdpr"
          />
          <Text style={{ marginBottom: 16 }} variant="titleEmphasized" tx="Settings.gdprSettings" />
          <RNText allowFontScaling={false} style={CONSENT}>
            {'You consented to the '}
            <TouchableOpacity onPress={() => history.push('/settings/gdpr/policy')}>
              <RNText style={CONSENT_BTN_TXT}>{'GDPR Consent Statement'}</RNText>
            </TouchableOpacity>
            {' on 12/12/2022 '}
          </RNText>
          <Button onPress={openGDPRModal} style={REQUEST_BTN}>
            <Text variant="body1Emphasized" tx="Settings.RequestMyData" />
          </Button>
          <Button onPress={revokeConsent} style={BUTTON}>
            <Text
              variant="body1Emphasized"
              style={{ color: color.red }}
              tx="Settings.revokeConsent"
            />
          </Button>
          <Button onPress={openDeleteModal} style={BUTTON}>
            <Text
              variant="body1Emphasized"
              style={{ color: color.red }}
              tx="Settings.deleteMyData"
            />
          </Button>
        </View>
      </View>
      {showDeleteModal && (
        <DualOptionModal
          title={t('Settings.Account.deleteAccount')}
          showModal={showDeleteModal}
          setShowModal={setShowDeleteModal}
          modalMessage={deleteModalMessage}
          continueHandler={confirmDeletion}
          yesBtnText={t('Settings.Account.yesDelete')}
          yesBtnColor={color.red}
        />
      )}
      {showGDPRModal && (
        <DualOptionModal
          title={t('Settings.RequestMyData')}
          showModal={showGDPRModal}
          setShowModal={setShowGDPRModal}
          modalMessage={gdprModalMessage}
          continueHandler={confirmRequest}
          yesBtnText={t('Common.yes')}
          yesBtnColor={color.blue}
        />
      )}
    </>
  )
})

/**
 * Styling
 */
const FULL: ViewStyle = { flex: 1 }
const CONTAINER: ViewStyle = {
  paddingTop: 40,
  paddingRight: 40,
  paddingLeft: 40,
  backgroundColor: color.white,
}
const HEADER_TITLE: TextStyle = { fontSize: 24, color: color.black }
const HEADER: ViewStyle = { marginBottom: 37 }
const BUTTON: ViewStyle = {
  backgroundColor: color.white,
  borderColor: color.red,
  borderRadius: 8,
  marginBottom: 20,
  borderWidth: 1,
}
const REQUEST_BTN: ViewStyle = {
  ...BUTTON,
  backgroundColor: color.shade04,
  borderColor: color.shade04,
}
const CONSENT: TextStyle = {
  marginBottom: 26,
  lineHeight: 20,
  fontWeight: '400',
  color: color.black,
  fontSize: 16,
  fontFamily: 'Inter',
}
const CONSENT_BTN_TXT: TextStyle = {
  lineHeight: 20,
  fontWeight: '600',
  fontSize: 16,
  fontFamily: 'Inter',
  color: color.blue,
}
